import React, { FunctionComponent, useState } from 'react';
import { useCommonStyles } from '../CommonStyles';
import Card from '../UI/Card';
import { getCards, CardData, CardTitles } from '../Data/CardData';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { hasPremiumAccess, useHasPremiumPlan } from 'hooks/premiumPlan';
import { ClientList } from './ClientList/ClientList';
import NotAvailable from '../NotAvailable/NotAvailable';
import VaccineStatus from './VaccineStatus/VaccineStatus';
import PetBirthday from './PetBirthday/PetBirthday';
import ClientInsights from './ClientInsights/ClientInsights';
import PetBreakdown from './PetBreakdown';
import SearchInput from '../components/SearchInput';
import ResultsNotFound from '../components/ResultsNotFound';
import store from 'store';

interface Props {
    title: string;
    category: string;
}

const ClientsAndPets: FunctionComponent<Props> = ({ title, category }) => {
    const commonClasses = useCommonStyles();
    const hasPremiumPlan = useHasPremiumPlan();

    const [selectedCard, setSelectedCard] = useState<CardData | null>(null);
    const [searchText, setSearchText] = useState('');
    const [showSearch, setShowSearch] = useState(true);

    const handleCardClick = (card: CardData) => {
        setSelectedCard(card);
        setShowSearch(false);
    };

    const showDefaultView = () => {
        setSelectedCard(null);
        setShowSearch(true);
    };

    const marketplaceId = store.getState().marketplace.marketplace.id;
    const cards = getCards(marketplaceId);

    const filteredCards = cards.filter(
        card =>
            card.category === category &&
            card.active &&
            (card.title.toLowerCase().includes(searchText.toLowerCase()) ||
                card.description.toLowerCase().includes(searchText.toLowerCase()))
    );

    const handleClickSearch = (): void => {
        setSearchText('');
    };

    const renderContent = () => {
        if (!selectedCard) {
            return (
                <div className={commonClasses.cardsContent}>
                    {filteredCards
                        .filter(card => card.category === category)
                        .map((card, index) => (
                            <Card
                                key={index}
                                title={card.title}
                                subTitle={card.category}
                                description={card.description}
                                premium={card.premium}
                                icon={card.icon}
                                onClick={() => {
                                    handleCardClick(card);
                                }}
                            />
                        ))}
                </div>
            );
        }

        // Return different components based on selected Card
        switch (selectedCard.title) {
            case CardTitles.CLIENT_LIST:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <ClientList description={selectedCard.description} />
                );
            case CardTitles.VACCINE_STATUS:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <VaccineStatus description={selectedCard.description} />
                );
            case CardTitles.PET_BIRTHDAY:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <PetBirthday description={selectedCard.description} />
                );
            case CardTitles.CLIENT_INSIGHTS:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <ClientInsights description={selectedCard.description} />
                );
            case CardTitles.PET_BREAKDOWN:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <PetBreakdown description={selectedCard.description} />
                );
            default:
                return null;
        }
    };

    const reportsNotFound = () => {
        return (
            <div className={commonClasses.resultNotFound}>
                <ResultsNotFound title="No reports found" />
            </div>
        );
    };

    return (
        <div className={commonClasses.container}>
            <div className={commonClasses.contentInsights}>
                <div className={commonClasses.subViewTitle}>
                    {selectedCard && (
                        <FontAwesomeIcon
                            className={commonClasses.backIcon}
                            icon={faArrowLeft}
                            onClick={showDefaultView}
                        />
                    )}
                    <h2 className={commonClasses.titleDefault}>{selectedCard ? selectedCard.title : title}</h2>
                </div>
                {showSearch && (
                    <SearchInput
                        searchText={searchText}
                        setSearchText={setSearchText}
                        showSearch={showSearch}
                        handleClickSearch={handleClickSearch}
                    />
                )}
                {filteredCards.length === 0 && reportsNotFound()}
                {renderContent()}
            </div>
        </div>
    );
};

export default ClientsAndPets;
